import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'

import { useStatusGateway, useWithdrawal } from './hooks'
import { requestForDealers, sendClientWDWOTrading } from './model'
import {
  Divider,
  Flex,
  Skeleton,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd'
import { EditOutlined, CopyOutlined } from '@ant-design/icons'
import { path, role } from '../../../../libs'
import { TransactionList } from '../../../../views/widgets/transactionList'
import { CommentList } from '../../../../views/widgets/commentList'
import { CommentForm } from '../../../../views/widgets/commentForm'
import { useStore, useUnit } from 'effector-react'
import { $userRole } from '../../../../features/user'
import { MoneyHistoryList } from '../../../../views/widgets/moneyHistoryList'
import { TransactionHelpModal } from '../../widgets/TransactionHelpModal/component'
import { ClientCard } from '../atoms/ClientCard/component'
import { AutoCheck } from '../atoms/AutoCheck/component'
import { mapDecisionStatusToOverall } from '../helpers/autoCheckStatus'
import { AutoCheckStatusTag } from '../atoms/AutoCheck/AutoCheckStatusTag'
import { GroupedClientPursesList } from '../../../../views/widgets/groupedClientPurses'
import { useClientPursesList } from '../../../../views/widgets/groupedClientPurses'
import {
  ClientPurseItem,
  AggregatedTurnover,
} from '../../../../views/widgets/groupedClientPurses/types'
import { unGroupClientPurseMounted } from '../../../../views/widgets/groupedClientPurses/model'
import {
  belongsToLocalMethod,
  removeDuplicateChildren,
  removeGroupElements,
} from '../../../../views/widgets/groupedClientPurses/utils'
import purseStyles from '../../../../views/widgets/groupedClientPurses/styles.module.css'
import { ConnectPurseModal } from '../../../../views/widgets/connectPurseModal/components'
import { $isRequestSuccess } from '../../../../features/notifications'
import { formatDigits } from '../../../../libs/utils'
import { Table, Button } from 'antd'
import { TableRowSelection } from 'antd/lib/table/interface'
import { ApiResult } from '../../../../components/apiResult/ApiResult'
import { FieldFormatter } from '../atoms/FormDataFields/component'
import { getRolesByGuardRules } from '../../../../features/session/constants'
import { WithdrawalEdit } from './types'
import { mappingBankValueWithFieldDataType } from '../../utils'
import { OrderInfo } from '../atoms/OrderInfo/OrderInfo'
import { WithdrawalButtons } from '../atoms/WithdrawalButtons'
import { StatusPanel } from '../atoms/StatusPanel/StatusPanel'
import { TransactionDetails } from '../atoms/TransactionDetails'
import { TransactionCommentsBlock } from '../atoms/TransactionCommentsBlock'
import { TransactionAmountsBlock } from '../atoms/TransactionAmountsBlock'
import { WithdrawalEditForm } from '../atoms/WithdrawalEditForm'
import { ChangeAmountsModal } from '../atoms/ChangeAmountsModal/component'
import { DisableAutoWithdrawalButton } from 'src/pages/billing/withdrawals/atoms/DisableAutoWithdrawalButton'
import { transactionRequestStatus } from '../../const'
import { ActionHistory } from '../../widgets/ActionHistory'
import { EditOwnerTypeModal } from 'src/pages/billing/withdrawals/atoms/EditOwnerTypeModal/component'
import { STATUS_OWNER_BE } from 'src/pages/billing/withdrawals/atoms/EditOwnerTypeModal/const'
import { useLocation, useNavigate } from 'react-router-dom'
import { MapGatewayStatusAntd } from 'src/pages/billing/paymentTrafficMonitoring/const'

const { Text } = Typography

// ——— Compact purses table ————————————————————————————————————
const aggregateByCurrency = (
  items: ClientPurseItem[],
): AggregatedTurnover[] => {
  const map = items.reduce<Record<string, AggregatedTurnover>>((acc, item) => {
    const cur = item.turnover.currency
    if (!acc[cur])
      acc[cur] = { currency: cur, deposit: 0, withdrawal: 0, total: 0 }
    acc[cur].total += item.turnover.total
    return acc
  }, {})
  return Object.values(map)
}

// The API marks local gateway rows with purseGroupId but keeps them flat.
// Restore the existing purse-group presentation under the Local Method layer.
const withLocalPurseGroups = (items: ClientPurseItem[]): ClientPurseItem[] =>
  items.map((item) => {
    if (item.rowType !== 'local_method') return item

    const groups = new Map<number, ClientPurseItem>()
    const ungrouped = [] as ClientPurseItem[]

    ;(item.children ?? []).forEach((child) => {
      if (!child.clientPurseId || !child.purseGroupId) {
        ungrouped.push(child)
        return
      }

      let group = groups.get(child.purseGroupId)
      if (!group) {
        group = {
          ...child,
          key: -(item.key * 1000000 + child.purseGroupId),
          rowType: 'group',
          gatewayId: null,
          clientPurseId: null,
          isTrusted: null,
          isMainInGroup: false,
          turnover: {
            currency: child.turnover.currency,
            deposit: 0,
            withdrawal: 0,
            total: 0,
            depositUSD: 0,
            withdrawalUSD: 0,
            totalUSD: 0,
          },
          children: [],
        }
        groups.set(child.purseGroupId, group)
      }

      const groupedPurse = group.children?.find(
        (candidate) => candidate.clientPurseId === child.clientPurseId,
      )

      if (groupedPurse) {
        groupedPurse.turnover.deposit += child.turnover.deposit
        groupedPurse.turnover.withdrawal += child.turnover.withdrawal
        groupedPurse.turnover.total =
          groupedPurse.turnover.deposit - groupedPurse.turnover.withdrawal
        groupedPurse.turnover.depositUSD += child.turnover.depositUSD
        groupedPurse.turnover.withdrawalUSD += child.turnover.withdrawalUSD
        groupedPurse.turnover.totalUSD += child.turnover.totalUSD

        if (child.isMainInGroup) {
          groupedPurse.isMainInGroup = true
          groupedPurse.bankName = child.bankName
          groupedPurse.mask = child.mask
          groupedPurse.clientName = child.clientName
          groupedPurse.ownerClassificationStatus =
            child.ownerClassificationStatus
        }
      } else {
        group.children?.push({
          ...child,
          rowType: 'purse',
          gatewayId: null,
        })
      }

      group.turnover.deposit += child.turnover.deposit
      group.turnover.withdrawal += child.turnover.withdrawal
      group.turnover.total = group.turnover.deposit - group.turnover.withdrawal
      group.turnover.depositUSD += child.turnover.depositUSD
      group.turnover.withdrawalUSD += child.turnover.withdrawalUSD
      group.turnover.totalUSD += child.turnover.totalUSD
    })

    groups.forEach((group) => {
      const representative =
        group.children?.find((child) => child.isMainInGroup) ??
        group.children?.[0]

      if (!representative) return

      group.bankName = representative.bankName
      group.mask = representative.mask
      group.clientName = representative.clientName
      group.ownerClassificationStatus = representative.ownerClassificationStatus
    })

    return {
      ...item,
      children: [...groups.values(), ...ungrouped],
    }
  })

const CompactPurses = ({ clientId }: { clientId: string }) => {
  const { clientPurses, loading } = useClientPursesList(clientId)
  const isRequestSuccess = useUnit($isRequestSuccess)
  const [selectedRows, setSelectedRows] = React.useState<ClientPurseItem[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([])
  const [isOpenEditOwnerTypeModal, setOpenEditOwnerTypeModal] = useState(false)
  const [selectedPurse, setSelectedPurse] = useState<ClientPurseItem | null>(
    null,
  )
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([])

  const rows = useMemo(() => withLocalPurseGroups(clientPurses), [clientPurses])

  const localMethodKeys = useMemo(
    () =>
      rows
        .filter((item) => item.rowType === 'local_method')
        .map((item) => item.key),
    [rows],
  )

  const localGatewayKeys = useMemo(
    () =>
      rows.flatMap((item) =>
        (item.children ?? [])
          .filter(
            (child) =>
              child.rowType === 'gateway' && (child.children?.length ?? 0) > 0,
          )
          .map((child) => child.key),
      ),
    [rows],
  )

  const localMethodLastRowKeys = useMemo(() => {
    const keys = new Set<React.Key>()

    rows.forEach((item) => {
      if (item.rowType !== 'local_method') return
      const children = item.children ?? []
      keys.add(
        children.length > 0 ? children[children.length - 1].key : item.key,
      )
    })

    return keys
  }, [rows])

  // Local Method is a visual, non-collapsible group. Gateways retain their
  // normal expand/collapse behaviour so purse details remain available.
  useEffect(() => {
    setExpandedRowKeys((keys) =>
      Array.from(new Set([...keys, ...localMethodKeys, ...localGatewayKeys])),
    )
  }, [localMethodKeys, localGatewayKeys])

  const isValidDisconnect = useMemo(() => {
    if (selectedRows.length < 2) return false
    const rows = removeGroupElements(selectedRows)
    return rows.every(
      (r) => r.purseGroupId === rows[0].purseGroupId && r.purseGroupId,
    )
  }, [selectedRows])

  useEffect(() => {
    if (isRequestSuccess) {
      setSelectedRows([])
      setSelectedRowKeys([])
    }
  }, [isRequestSuccess])

  const rowSelection: TableRowSelection<ClientPurseItem> = {
    checkStrictly: false,
    selectedRowKeys,
    getCheckboxProps: (record) => ({
      disabled: belongsToLocalMethod(record),
    }),
    renderCell: (_checked, record, _index, originNode) =>
      belongsToLocalMethod(record) ? null : originNode,
    onChange: (keys, rows) => {
      setSelectedRowKeys(keys)
      setSelectedRows(rows)
    },
    onSelect: (_, __, rows) => setSelectedRows(rows),
  }

  const getTotalText = (item: ClientPurseItem) => {
    if (item.children && item.children.length > 0) {
      const breakdown = aggregateByCurrency(item.children).filter(
        (entry) => entry.total !== 0,
      )

      if (item.turnover.totalUSD !== 0) {
        return `${formatDigits(item.turnover.totalUSD)} USD (${breakdown
          .map((entry) => `${formatDigits(entry.total)} ${entry.currency}`)
          .join(', ')})`
      }

      if (breakdown.length === 1) {
        const [entry] = breakdown
        return `${formatDigits(entry.total)} ${entry.currency}`
      }

      return breakdown.length > 0
        ? breakdown
            .map((entry) => `${formatDigits(entry.total)} ${entry.currency}`)
            .join(', ')
        : `${formatDigits(item.turnover.total)} ${item.turnover.currency}`
    }
    return `${formatDigits(item.turnover.total)} ${item.turnover.currency}`
  }

  const getTotalColor = (item: ClientPurseItem) => {
    const value = item.children?.length
      ? item.turnover.totalUSD ||
        item.children.reduce(
          (total, child) =>
            total + (child.turnover.totalUSD || child.turnover.total),
          0,
        )
      : item.turnover.total
    return value > 0 ? 'green' : value < 0 ? 'red' : undefined
  }

  const columns = [
    {
      title: 'Bank',
      render: (item: ClientPurseItem) => {
        if (item.rowType === 'local_method') {
          return <strong>{`LOCAL / ${item.localMethodCountry || '?'}`}</strong>
        }
        return item.bankName
      },
    },
    {
      title: 'Client Name',
      dataIndex: 'clientName',
      render: (text: string | null) => <strong>{text ? text : ''}</strong>,
    },
    {
      title: 'Purse',
      render: (item: ClientPurseItem) => (
        <strong style={{ wordBreak: 'break-all' }}>{item.mask}</strong>
      ),
    },
    {
      title: 'Group ID',
      render: (item: ClientPurseItem) =>
        item.purseGroupId ? <strong>{item.purseGroupId}</strong> : '-',
    },

    {
      title: 'Total',
      render: (item: ClientPurseItem) => (
        <span style={{ color: getTotalColor(item) }}>{getTotalText(item)}</span>
      ),
    },
    {
      title: 'Owner Type',
      dataIndex: 'ownerClassificationStatus',
      render: (type: string, item: ClientPurseItem) => {
        const editButton = item.clientPurseId ? (
          <Tooltip title="Override owner type">
            <Button
              type="text"
              color="blue"
              icon={
                <EditOutlined
                  onClick={() => {
                    setOpenEditOwnerTypeModal(true)
                    setSelectedPurse(item)
                  }}
                  style={{
                    color: 'var(--color-primary)',
                    cursor: 'pointer',
                  }}
                />
              }
            />
          </Tooltip>
        ) : null

        const getContent = () => {
          if (!type) {
            return <strong>-</strong>
          }

          if (type === STATUS_OWNER_BE) {
            return (
              <Tag color="green-inverse" style={{ userSelect: 'none' }}>
                Own
              </Tag>
            )
          }

          return (
            <Tag color="volcano-inverse" style={{ userSelect: 'none' }}>
              Third-party
            </Tag>
          )
        }

        return (
          <Flex justify="space-between" align="center">
            {getContent()}
            {editButton}
          </Flex>
        )
      },
    },
    {
      title: '',
      width: 40,
      render: (item: ClientPurseItem) =>
        belongsToLocalMethod(item) ? null : (
          <Tooltip title="Copy purse and total">
            <CopyOutlined
              style={{ cursor: 'pointer', color: 'var(--color-primary)' }}
              onClick={(event) => {
                event.stopPropagation()
                const text = [item.mask, getTotalText(item)]
                  .filter(Boolean)
                  .join(' ')
                if (!text) return
                navigator.clipboard?.writeText(text)
                message.success('Copied')
              }}
            />
          </Tooltip>
        ),
    },
  ]

  return (
    <div>
      <ConnectPurseModal
        purses={removeDuplicateChildren(selectedRows)}
        clientId={clientId}
      />
      <Button
        color="purple"
        variant="solid"
        style={{ marginLeft: 8 }}
        disabled={!isValidDisconnect}
        onClick={() => {
          if (
            window.confirm(
              'Are you sure you want to unlink the selected wallets?',
            )
          ) {
            const rows = removeGroupElements(selectedRows)
            if (isValidDisconnect) {
              unGroupClientPurseMounted({
                clientPursesIds: rows
                  .filter((r) => r.clientPurseId)
                  .map((r) => r.clientPurseId as number),
              })
            }
          }
        }}
      >
        Disconnect purses
      </Button>
      <Table
        dataSource={rows}
        columns={columns}
        loading={loading}
        size="small"
        pagination={false}
        scroll={{ x: true }}
        rowKey="key"
        rowSelection={rowSelection}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) =>
            setExpandedRowKeys(
              Array.from(new Set([...keys, ...localMethodKeys])),
            ),
          expandIcon: ({ expanded, onExpand, record }) => {
            if (record.rowType === 'local_method') return null
            if (!record.children?.length) {
              return (
                <span className="ant-table-row-expand-icon ant-table-row-expand-icon-spaced" />
              )
            }
            return expanded ? (
              <span
                className="ant-table-row-expand-icon ant-table-row-expand-icon-expanded"
                onClick={(event) => onExpand(record, event)}
              />
            ) : (
              <span
                className="ant-table-row-expand-icon ant-table-row-expand-icon-collapsed"
                onClick={(event) => onExpand(record, event)}
              />
            )
          },
        }}
        rowClassName={(record) => {
          if (belongsToLocalMethod(record)) {
            return [
              purseStyles.localMethodBlock,
              record.rowType === 'local_method'
                ? purseStyles.localMethodFirst
                : '',
              localMethodLastRowKeys.has(record.key)
                ? purseStyles.localMethodLast
                : '',
              record.rowType === 'gateway' && (record.children?.length ?? 0) > 0
                ? purseStyles.tableRowAlert
                : '',
              record.clientPurseId && record.purseGroupId
                ? purseStyles.tableRowChildrenAlert
                : '',
            ]
              .filter(Boolean)
              .join(' ')
          }
          if (record.purseGroupId && !record.clientPurseId) {
            return purseStyles.tableRowAlert
          }
          if (record.clientPurseId && record.purseGroupId) {
            return purseStyles.tableRowChildrenAlert
          }
          return ''
        }}
      />
      <EditOwnerTypeModal
        open={isOpenEditOwnerTypeModal}
        setOpen={setOpenEditOwnerTypeModal}
        purse={selectedPurse}
      />
    </div>
  )
}

// ——— CSS tokens ————————————————————————————————————————————
// All colours come from var(--crm-*) so light/dark just works.

const s = {
  infoCard: {
    background: 'var(--crm-bg)',
    border: '1px solid var(--crm-border)',
    borderRadius: 8,
    padding: 16,
  } as React.CSSProperties,

  infoCardTitle: {
    fontSize: 10,
    fontWeight: 600,
    color: 'var(--crm-text-secondary)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    marginBottom: 12,
  } as React.CSSProperties,

  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: '5px 0',
    borderBottom: '1px solid var(--crm-border)',
  } as React.CSSProperties,

  infoRowLast: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: '5px 0',
  } as React.CSSProperties,

  infoRowLeft: {
    display: 'flex',
    gap: 12,
    alignItems: 'baseline',
    padding: '5px 0',
    borderBottom: '1px solid var(--crm-border)',
  } as React.CSSProperties,

  labelLeft: {
    fontSize: 14,
    color: 'var(--crm-text-secondary)',
    flexShrink: 0,
  } as React.CSSProperties,

  valueLeft: {
    fontSize: 16,
    fontWeight: 600,
    fontFamily: 'monospace',
    wordBreak: 'break-all' as const,
  } as React.CSSProperties,

  label: {
    fontSize: 14,
    color: 'var(--crm-text-secondary)',
    flexShrink: 0,
    marginRight: 8,
  } as React.CSSProperties,

  value: {
    fontSize: 14,
    fontWeight: 500,
    fontFamily: 'monospace',
    textAlign: 'right' as const,
    maxWidth: '65%',
    wordBreak: 'break-all' as const,
  } as React.CSSProperties,

  sidebarTitle: {
    fontSize: 10,
    fontWeight: 600,
    color: 'var(--crm-text-secondary)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    marginBottom: 10,
  } as React.CSSProperties,

  sidebarCard: {
    background: 'var(--crm-bg-elevated)',
    border: '1px solid var(--crm-border)',
    borderRadius: 8,
    padding: 14,
  } as React.CSSProperties,

  hr: {
    border: 'none',
    borderTop: '1px solid var(--crm-border)',
    margin: '16px 0',
  } as React.CSSProperties,
}

// ——— Purse type mapping ———————————————————————————————————
const PURSE_TYPE_LABELS: Record<string, string> = {
  CLIENT_PURSE_TYPE_OTHER: 'Other',
  CLIENT_PURSE_TYPE_CARD: 'Card',
  CLIENT_PURSE_TYPE_BANK: 'Bank account',
  CLIENT_PURSE_TYPE_EWALLET: 'E-wallet',
  CLIENT_PURSE_TYPE_CRYPTO: 'Crypto wallet',
  CLIENT_PURSE_TYPE_MOBILE: 'Mobile',
}

function formatPurseType(raw?: string): string {
  if (!raw) return '—'
  return (
    PURSE_TYPE_LABELS[raw] ??
    raw
      .replace(/^CLIENT_PURSE_TYPE_/, '')
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/^\w/, (c) => c.toUpperCase())
  )
}

// ——— InfoRow helper ———————————————————————————————————————
function InfoRow({
  label,
  value,
  last = false,
  large = false,
}: {
  label: string
  value: React.ReactNode
  last?: boolean
  large?: boolean
}) {
  if (value === null || value === undefined || value === '') return null
  if (large) {
    return (
      <div style={s.infoRowLeft}>
        <span style={s.labelLeft}>{label}</span>
        <span style={s.valueLeft}>{value}</span>
      </div>
    )
  }
  return (
    <div style={last ? s.infoRowLast : s.infoRow}>
      <span style={s.label}>{label}</span>
      <span style={s.value}>{value}</span>
    </div>
  )
}

export const WithdrawalEditDetail = () => {
  const [isOpenModal, setIsOpenModal] = useState(false)
  const { pathname } = useLocation()
  const navigate = useNavigate()

  const {
    withdrawal,
    loading,
    actionLoading,
    errorAmount,
    success,
    dividendBalance,
    error,
  } = useWithdrawal()

  const mapStatusGatewayByCountries = useStatusGateway()
  const country = withdrawal?.client?.country
  const statusGateway =
    mapStatusGatewayByCountries && country
      ? mapStatusGatewayByCountries?.[country]
      : undefined

  const currentOriginHost = window.location.origin
  const userRole = useStore($userRole)
  const isLoadingRequestForDealers = useUnit(requestForDealers.pending)
  const [activeTab, setActiveTab] = useState('detail')
  const [visitedTabs, setVisitedTabs] = useState(() => new Set(['detail']))

  useEffect(() => {
    setActiveTab('detail')
    setVisitedTabs(new Set(['detail']))
  }, [pathname])

  const handleTabChange = (key: string) => {
    setActiveTab(key)
    setVisitedTabs((tabs) => {
      if (tabs.has(key)) return tabs
      const nextTabs = new Set(tabs)
      nextTabs.add(key)
      return nextTabs
    })
  }

  const getSourceId = useMemo(() => {
    if (withdrawal.destinationType === 'account') {
      const source = withdrawal.client.accounts.find(
        (item) => item.accountId === withdrawal.destinationId,
      )
      return source
        ? `${source.accountPlatform} ${source.mtLogin}`
        : 'Not found'
    }
    const source = withdrawal.client.wallets.find(
      (item) => item.walletId === withdrawal.destinationId,
    )
    return source ? `Wallet ${source.currency}` : 'Not found'
  }, [withdrawal.client, withdrawal.destinationType, withdrawal.destinationId])

  const textForCopy = useMemo(
    () =>
      `
RDC Link: https://rdc.hwmt.link/module/query/info-by-client?id=${withdrawal.client.clientId}
User ID: ${withdrawal.client.clientId}
Client Link: ${currentOriginHost}/client/${withdrawal.client.clientId}
WD Link: ${currentOriginHost}/withdrawal/edit/${withdrawal.transactionId}
Full name: ${withdrawal.client.fullName}
E-mail: ${withdrawal.client.email}
Client amount: ${withdrawal.clientAmount} ${withdrawal.clientCurrency}
Source: ${getSourceId}
    `.trim(),
    [withdrawal, currentOriginHost],
  )

  const decision = withdrawal.withdrawalDecisions
  const overallState = mapDecisionStatusToOverall(
    decision?.status,
    decision?.approved,
  )
  const canEditGateway = getRolesByGuardRules('edit_gateway').some((r) =>
    userRole?.includes(r),
  )

  const formatters: Record<string, FieldFormatter> = {
    bank: (value, _name, withdrawal: WithdrawalEdit) =>
      mappingBankValueWithFieldDataType(String(value), withdrawal),
    bankName: (value, _name, withdrawal: WithdrawalEdit) =>
      mappingBankValueWithFieldDataType(String(value), withdrawal),
    bankGroupID: (value, _name, withdrawal: WithdrawalEdit) =>
      mappingBankValueWithFieldDataType(String(value), withdrawal),
    amount: (value) => String(value),
  }

  const canDisableAutoWithdrawal =
    withdrawal.status.toLocaleUpperCase() ===
    transactionRequestStatus.pendingAutoCheck()

  const transactionHistory = useMemo(
    () =>
      withdrawal.clientId ? (
        <TransactionList
          key={withdrawal.clientId}
          entityId={withdrawal.clientId}
        />
      ) : null,
    [withdrawal.clientId],
  )

  // —— SKELETON ————————————————————————————————————————————
  if (loading && !withdrawal.transactionId) {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        <div
          style={{
            width: 360,
            flexShrink: 0,
            padding: 20,
            borderRight: '1px solid var(--crm-border)',
          }}
        >
          <div style={s.sidebarCard}>
            <Skeleton active paragraph={{ rows: 3 }} />
          </div>
          <hr style={s.hr} />
          <div style={s.sidebarCard}>
            <Skeleton active paragraph={{ rows: 2 }} />
          </div>
        </div>
        <div style={{ flex: 1, padding: '20px 24px' }}>
          <Flex gap={10} align="center" style={{ marginBottom: 20 }}>
            <Skeleton.Button active size="small" style={{ width: 70 }} />
            <Skeleton.Input active size="small" style={{ width: 180 }} />
            <Skeleton.Button active size="small" style={{ width: 55 }} />
          </Flex>
          <Flex gap={8} style={{ marginBottom: 20 }}>
            {[110, 80, 90].map((w, i) => (
              <Skeleton.Button
                key={i}
                active
                size="small"
                style={{ width: w }}
              />
            ))}
          </Flex>
          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}
          >
            <div style={s.infoCard}>
              <Skeleton active paragraph={{ rows: 4 }} />
            </div>
            <div style={s.infoCard}>
              <Skeleton active paragraph={{ rows: 4 }} />
            </div>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 10,
              marginTop: 14,
            }}
          >
            {[1, 2, 3, 4].map((i) => (
              <div key={i} style={s.infoCard}>
                <Skeleton active paragraph={{ rows: 2 }} title={false} />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // —— DETAIL TAB ——————————————————————————————————————————
  const detailContent = (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 24,
        alignItems: 'flex-start',
      }}
    >
      <div>
        <>
          {/* Status buttons */}
          <div
            style={{
              display: 'flex',
              gap: 6,
              marginBottom: 14,
              flexWrap: 'wrap',
            }}
          >
            <StatusPanel
              withdrawal={withdrawal}
              userRole={userRole}
              actionLoading={actionLoading}
            />
            {canDisableAutoWithdrawal && (
              <DisableAutoWithdrawalButton
                transactionId={withdrawal.transactionId}
              />
            )}
          </div>

          {/* Manager confirmation info */}
          {(withdrawal.changeHistory?.user?.email ||
            withdrawal.humanConfirmedByManagerAt ||
            withdrawal.humanConfirmedByVendorAt) && (
            <Flex
              gap={16}
              style={{ marginBottom: 16, fontSize: 14 }}
              wrap="wrap"
            >
              {withdrawal.changeHistory?.user?.email && (
                <Text type="secondary">
                  Manager: {withdrawal.changeHistory.user.email}
                </Text>
              )}
              {withdrawal.humanConfirmedByManagerAt && (
                <Text type="secondary">
                  Confirmed by manager: {withdrawal.humanConfirmedByManagerAt}{' '}
                  (GMT+3)
                </Text>
              )}
              {withdrawal.humanConfirmedByVendorAt && (
                <Text type="secondary">
                  Confirmed by vendor: {withdrawal.humanConfirmedByVendorAt}{' '}
                  (GMT+3)
                </Text>
              )}
            </Flex>
          )}

          {/* Transaction data (left) + Comments type=0 (right) */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 14,
              alignItems: 'flex-start',
            }}
          >
            {/* Transaction data */}
            <div style={{ ...s.infoCard }}>
              <div style={s.infoCardTitle}>Transaction data</div>

              {withdrawal.gateway?.internalName && (
                <div style={s.infoRowLeft}>
                  <span style={s.labelLeft}>Gateway</span>
                  <span style={s.valueLeft}>
                    {withdrawal.gateway.gatewayId &&
                      (canEditGateway ? (
                        <a
                          href={path.billing.gateway.edit(
                            withdrawal.gateway.gatewayId.toString(),
                          )}
                          target="_blank"
                        >
                          {withdrawal.gateway.gatewayId}
                        </a>
                      ) : (
                        <span>{withdrawal.gateway.gatewayId}</span>
                      ))}{' '}
                    {withdrawal.gateway.internalName}
                  </span>
                </div>
              )}
              {Boolean(withdrawal.gatewayId) && (
                <div style={s.infoRowLeft}>
                  <span style={s.labelLeft}>Gateway Status</span>
                  <Tag
                    color={
                      statusGateway
                        ? MapGatewayStatusAntd[statusGateway]
                        : 'default'
                    }
                    style={{ cursor: 'pointer', color: 'black' }}
                    onClick={() => {
                      window.open(
                        path.billing.paymentTrafficMonitoring.entry({
                          name: 'searchField_gatewayId',
                          value: withdrawal.gatewayId,
                        }),
                        '_blank',
                      )
                    }}
                  >
                    {statusGateway ?? 'Unknown'}
                  </Tag>
                </div>
              )}
              {withdrawal.transactionId !== '' && (
                <TransactionDetails
                  withdrawal={withdrawal}
                  displayMode="collapse"
                  fieldFormatters={formatters}
                />
              )}

              {(withdrawal.externalTransaction?.externalId ||
                withdrawal.externalPaymentTransaction?.externalId) && (
                <InfoRow
                  label="External ID"
                  value={
                    withdrawal.externalTransaction?.externalId ||
                    withdrawal.externalPaymentTransaction?.externalId
                  }
                  large
                  last
                />
              )}

              <TransactionCommentsBlock withdrawal={withdrawal} />
            </div>

            {/* Comments type=0 */}
            {withdrawal.clientId !== '' && (
              <CommentList
                entityType="client"
                entityId={withdrawal.clientId}
                types={[0]}
                columns={1}
              />
            )}
          </div>

          {/* Amounts — 4 columns */}
          <TransactionAmountsBlock
            withdrawal={withdrawal}
            loading={loading}
            errorAmount={errorAmount}
            success={success}
          />

          {/* Gateway row */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginTop: 14,
            }}
          >
            <WithdrawalEditForm
              loading={loading}
              withdrawal={withdrawal}
              error={error}
            />
            <ChangeAmountsModal
              disabled={loading}
              withdrawal={withdrawal}
              errorAmount={errorAmount}
              success={success}
            />
          </div>

          {/* Order info */}
          {withdrawal?.order && (
            <div style={{ marginTop: 16 }}>
              <OrderInfo order={withdrawal.order} />
            </div>
          )}

          {withdrawal.clientId !== '' && (
            <>
              <Divider />
              <CommentList
                entityType="client"
                entityId={withdrawal.clientId}
                types={[1, 2]}
                columns={3}
              />
            </>
          )}

          {/* Payment info + Client purse — 2 columns */}
          <Divider />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 14,
            }}
          >
            {/* Payment info */}
            <div style={s.infoCard}>
              <div style={s.infoCardTitle}>Payment info</div>
              <InfoRow
                label="Destination"
                value={`${withdrawal.destinationType}${
                  withdrawal.destinationId
                    ? ` — ${withdrawal.destinationId}`
                    : ''
                }`}
              />
              <InfoRow label="Source" value={getSourceId} />
              <InfoRow
                label="Processing"
                value={withdrawal.processingProvider}
              />
              <InfoRow
                label="Method"
                value={withdrawal.gateway?.paymentMethodType}
              />
              <InfoRow
                label="External ID"
                value={
                  withdrawal.externalTransaction?.externalId ||
                  withdrawal.externalPaymentTransaction?.externalId
                }
              />
              <InfoRow
                label="MT Transaction"
                value={withdrawal.mtTransactionId || null}
                last
              />
            </div>

            {/* Client purse */}
            <div style={s.infoCard}>
              <div style={s.infoCardTitle}>Client purse</div>
              {withdrawal.clientPurse ? (
                <>
                  <InfoRow label="Mask" value={withdrawal.clientPurse.mask} />
                  <InfoRow
                    label="Type"
                    value={formatPurseType(withdrawal.clientPurse.type)}
                  />
                  <InfoRow
                    label="Expiry"
                    value={withdrawal.clientPurse.expiry}
                  />
                  <InfoRow
                    label="Status"
                    value={withdrawal.clientPurse.status?.replace(
                      'CLIENT_PURSE_STATUS_',
                      '',
                    )}
                  />
                  <InfoRow
                    label="Name"
                    value={withdrawal.clientPurse.clientName}
                  />
                  <InfoRow
                    label="Bank"
                    value={withdrawal.clientPurse.bankName}
                  />
                  {withdrawal.clientPurse.isCard && (
                    <InfoRow
                      label="Verification"
                      value={
                        <a
                          href={path.cardVerifications.details(
                            withdrawal.clientPurseId.toString(),
                          )}
                          target="_blank"
                        >
                          View ↗
                        </a>
                      }
                      last
                    />
                  )}
                </>
              ) : (
                <span
                  style={{ fontSize: 14, color: 'var(--crm-text-secondary)' }}
                >
                  —
                </span>
              )}
            </div>
          </div>

          {/* Vendor data — full row below */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 14,
              marginTop: 14,
            }}
          >
            <div style={s.infoCard}>
              <div style={s.infoCardTitle}>Vendor data</div>
              {withdrawal.externalPaymentTransaction ? (
                <>
                  <InfoRow
                    label="Status"
                    value={withdrawal.externalPaymentTransaction.externalStatus}
                  />
                  <InfoRow
                    label="Initial amount"
                    value={`${withdrawal.externalPaymentTransaction.initialAmount} ${withdrawal.externalPaymentTransaction.initialCurrency}`}
                  />
                  <InfoRow
                    label="Paid amount"
                    value={`${withdrawal.externalPaymentTransaction.paidAmount} ${withdrawal.externalPaymentTransaction.paidCurrency}`}
                  />
                  <InfoRow
                    label="Commission"
                    value={`${withdrawal.externalPaymentTransaction.vendorCommission} ${withdrawal.externalPaymentTransaction.vendorCommissionCurrency}`}
                    last
                  />
                </>
              ) : (
                <span
                  style={{ fontSize: 14, color: 'var(--crm-text-secondary)' }}
                >
                  —
                </span>
              )}
            </div>
          </div>
        </>
      </div>
      {/* /left col */}

      {/* Right col: compact grouped purses */}
      {withdrawal.clientId && <CompactPurses clientId={withdrawal.clientId} />}
    </div>
  )

  const tabItems = [
    { key: 'detail', label: 'Detail', children: detailContent },
    {
      key: 'history',
      label: 'History',
      children: null,
    },
    {
      key: 'money',
      label: 'Money history',
      children: withdrawal.clientId ? (
        <MoneyHistoryList
          entityId={withdrawal.clientId}
          client={{
            name: withdrawal.client?.fullName,
            email: withdrawal.client?.email,
            country: withdrawal.client?.country,
          }}
        />
      ) : null,
    },
    {
      key: 'grouped',
      label: 'Client grouped purses',
      children: withdrawal.clientId ? (
        <GroupedClientPursesList entityId={withdrawal.clientId} />
      ) : null,
    },
    {
      key: 'autocheck',
      label: 'Automatic withdrawal',
      children: <AutoCheck withdrawal={withdrawal} />,
    },
    {
      key: 'actionhistory',
      label: 'Action history',
      children: (
        <ActionHistory
          history={withdrawal.changeHistoryAll}
          createdAt={withdrawal.createdAt}
          acceptedAt={withdrawal.confirmedByVendorAt}
        />
      ),
    },
  ]

  // —— RENDER ——————————————————————————————————————————————
  return (
    <div>
      {/* Action buttons topbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 20px 8px calc(360px + 20px)',
          borderBottom: '1px solid var(--crm-border)',
        }}
      >
        <WithdrawalButtons
          withdrawal={withdrawal}
          textForCopy={textForCopy}
          loading={loading}
          isLoadingRequestForDealers={isLoadingRequestForDealers}
          setIsOpenModal={setIsOpenModal}
        />
      </div>

      <ApiResult
        effect={sendClientWDWOTrading}
        successMessage="The request to notify the client about the withdrawal without trading has been successfully sent."
      />

      {/* Main layout */}
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {/* Sidebar (left) */}
        <div
          style={{
            width: 360,
            flexShrink: 0,
            position: 'sticky',
            top: 80,
            maxHeight: '100vh',
            overflowY: 'auto',
            padding: 20,
            background: 'var(--crm-bg)',
            borderRight: '1px solid var(--crm-border)',
          }}
        >
          <ClientCard
            withdrawal={withdrawal}
            dividendBalance={dividendBalance}
          />

          <hr style={s.hr} />

          {userRole &&
            !userRole.includes(role.clientSupport.supportAgent()) &&
            !userRole.includes(role.clientSupport.supportManager()) && (
              <div>
                <CommentForm type="client" entityId={withdrawal.clientId} />
              </div>
            )}
        </div>

        {/* Content (right) */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          {/* Card header + tabs strip */}
          <div
            style={{
              background: 'var(--crm-bg)',
              borderBottom: '1px solid var(--crm-border)',
              padding: '16px 24px 0',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'stretch', gap: 24 }}>
              {/* Left col: title + tabs */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <Flex align="center" gap={10} style={{ marginBottom: 6 }}>
                  <Tag
                    color={
                      withdrawal.product === 'headway' ? '#dbb454' : '#50f9ac'
                    }
                    style={{
                      color: '#000',
                      margin: 0,
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {withdrawal.product}
                  </Tag>
                  <span
                    style={{
                      fontSize: 17,
                      fontWeight: 600,
                      fontFamily: 'monospace',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    withdrawal {withdrawal.transactionId}
                  </span>
                  <span
                    style={{
                      background: 'var(--crm-bg-elevated)',
                      border: '1px solid var(--crm-border)',
                      color: 'var(--crm-text-secondary)',
                      fontSize: 10,
                      fontWeight: 500,
                      padding: '2px 8px',
                      borderRadius: 3,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {withdrawal.isAuto ? 'Auto' : 'Manual'}
                  </span>
                  {decision && <AutoCheckStatusTag state={overallState} />}
                </Flex>
                <Tabs
                  activeKey={activeTab}
                  onChange={handleTabChange}
                  size="small"
                  style={{ marginBottom: -1 }}
                  items={tabItems.map(({ key, label }) => ({ key, label }))}
                />
              </div>

              {/* Right col: dates */}
              <div
                style={{
                  textAlign: 'right',
                  fontSize: 14,
                  color: 'var(--crm-text-secondary)',
                  lineHeight: 1.6,
                  fontFamily: 'monospace',
                  flexShrink: 0,
                  paddingTop: 2,
                }}
              >
                {withdrawal.humanCreatedAt && (
                  <div>
                    Created{' '}
                    <strong
                      style={{
                        fontSize: 18,
                        color: 'var(--crm-text-primary, inherit)',
                        fontWeight: 600,
                      }}
                    >
                      {withdrawal.humanCreatedAt}
                    </strong>{' '}
                    GMT+3
                  </div>
                )}
                {withdrawal.humanUpdatedAt && (
                  <div
                    style={{
                      color:
                        withdrawal.humanUpdatedAt !== withdrawal.humanCreatedAt
                          ? 'var(--crm-accent-amber, #f59e0b)'
                          : undefined,
                    }}
                  >
                    Updated{' '}
                    <strong style={{ fontSize: 18, fontWeight: 600 }}>
                      {withdrawal.humanUpdatedAt}
                    </strong>{' '}
                    GMT+3
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tab content */}
          <div style={{ padding: '16px 24px' }}>
            {withdrawal.clientId && (
              <div
                style={{ display: activeTab === 'history' ? 'block' : 'none' }}
              >
                {transactionHistory}
              </div>
            )}
            {tabItems.map(
              ({ key, children }) =>
                key !== 'history' &&
                visitedTabs.has(key) && (
                  <div
                    key={key}
                    style={{ display: activeTab === key ? 'block' : 'none' }}
                  >
                    {children}
                  </div>
                ),
            )}
          </div>
        </div>
      </div>

      {isOpenModal && (
        <TransactionHelpModal
          transactionId={Number(withdrawal.transactionId)}
          isVisible={isOpenModal}
          onClose={() => setIsOpenModal(false)}
        />
      )}
    </div>
  )
}
